/**
 * HTTP client utilities for Easyblocks API
 */

export class EasyblocksApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "EasyblocksApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * Read environment variable from Vite config
 */
export function readViteEnv(name: string, defaultValue: string): string {
  const env = (import.meta as any)?.env;
  const v = env?.[name];
  return typeof v === "string" && v.length > 0 ? v : defaultValue;
}

/**
 * Join base URL with path segment
 */
export function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

/**
 * Get backend kind from environment
 */
export function getBackendKind(): string {
  return readViteEnv("VITE_EASYBLOCKS_BACKEND", "php").toLowerCase();
}

/**
 * Get PHP API base URL from environment
 */
export function getApiBaseUrl(): string {
  return readViteEnv("VITE_EASYBLOCKS_PHP_API_BASE", "/api/easyblocks");
}

/**
 * Safely parse JSON response, returning text as fallback
 */
export async function readJsonSafely(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * Fetch JSON with automatic error handling
 */
export async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const res = await fetch(input, init);
  if (!res.ok) {
    const body = await readJsonSafely(res);
    throw new EasyblocksApiError(
      `API request failed (${res.status} ${res.statusText})`,
      res.status,
      body
    );
  }
  return (await res.json()) as T;
}
